<template>
  <div class="user-research">
    <transition name="slide-fade">
      <div class="user-research-wrap">
        <div class="user-research-header">
          <div class="back">
            <ui-button :iconName="'icon--arrow-left-gray'"
                       @click="toDashboard"></ui-button>
          </div>

          <div v-if="domain"
               class="title">
            <div class="main-button"
                 :class="{ off: mainDomain !== domain.orgSid }">
              <button class="to-main"
                      @click="setMainDomain(true)">
                <i class="icon dashboard--star off"></i>
                <i class="icon dashboard--star"></i>
              </button>
            </div>
            <h3 class="name">{{ domain.domainName }}</h3>
            <h4 class="domain">{{ domain.domain }}</h4>
          </div>
        </div>
        <div class="user-research-body">
          <transition name="fade">
            <div v-if="loading"
                 class="loader">
              <ba-loading :applyBackground="false"
                          :showLoadingText="true"/>
            </div>
          </transition>

          <div class="body-header">
            <div class="tab--wrap">
              <div class="tab"
                   :class="tabType">
                <button type="button"
                        class="ut"
                        :class="{ on: tabType === 'ut' }"
                        @click="onSelectTabType('ut')">
                  <span class="soon">
                    Coming Soon
                  </span>
                  {{ $t('userResearch.ut.title') }}
                </button>
                <button type="button"
                        class="survey"
                        :class="{ on: tabType === 'survey' }"
                        @click="onSelectTabType('survey')">
                  {{ $t('userResearch.survey.title') }}
                </button>
              </div>
            </div>
            <div class="buttons"
                 v-if="items.length">
              <div class="search"
                   :class="{ ing: computedSearching }"
                   @click="startSearch">
                <input v-model="keyword"
                       type="text"
                       ref="search"
                       @focus="searching = true"
                       @blur="searching = false" />
                <i class="icon icon--search"></i>
              </div>
              <div class="add">
                <ui-button :size="'large'"
                           :primary2="true"
                           :disabled="isDemo"
                           @click="go({ name: 'addSurvey', query: { sid: $route.query.sid } })">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
                    <g fill="#fff" fill-rule="evenodd" transform="translate(2 2)">
                      <rect width="12" height="2" y="5" rx="1"/>
                      <rect width="12" height="2" y="5" rx="1" transform="rotate(90 6 6)"/>
                    </g>
                  </svg>
                  {{ $t('userResearch.survey.createOne') }}
                </ui-button>
              </div>
            </div>
          </div>

          <div class="body-list"
               :style="{ marginRight: `-${scrollbarWidth}px` }">
            <div v-if="computedItems.length"
                 class="thead">
              <table :style="{ width: `calc(100% - ${scrollbarWidth}px)` }">
                <colgroup>
                  <col>
                  <col width="110px">
                  <col width="110px">
                  <col width="110px">
                  <col width="110px">
                  <col width="50px">
                  <col width="50px">
                </colgroup>
                <thead>
                  <tr>
                    <th v-for="header in headers"
                        :key="`header-${header.sortColumn}`"
                        :class="[`th-${header.sortColumn}`, { sortable: !notSortable.includes(header.sortColumn) }]"
                        @click="changeSort(header.sortColumn)">
                      <span>{{ header.name }}</span>
                      <button v-if="!['view', 'more'].includes(header.sortColumn)"
                              class="sort"
                              :class="{ active: items.length && sortColumn === header.sortColumn,
                                        asc: isSortAsc,
                                        desc: !isSortAsc }">
                        <i class="icon icon--sort-asc"></i>
                        <i class="icon icon--sort-desc"></i>
                      </button>
                    </th>
                  </tr>
                </thead>
              </table>
            </div>
            <div v-if="computedItems.length"
                 class="tbody">
              <table>
                <colgroup>
                  <col>
                  <col width="110px">
                  <col width="110px">
                  <col width="110px">
                  <col width="110px">
                  <col width="50px">
                  <col width="50px">
                </colgroup>
                <tbody :key="computedItems.length">
                  <tr v-for="(item, index) in computedItems"
                      :key="index">
                    <td v-for="header in headers"
                        :key="`data-${index}-${header.sortColumn}`"
                        :class="`td-${header.sortColumn}`">
                      <span v-if="header.sortColumn === 'surveyName'"
                            class="name">
                        <text-highlight :searchKeyword="keyword"
                                        :textItem="item[header.sortColumn]"></text-highlight>
                      </span>
                      <span v-else-if="header.sortColumn === 'startDate' || header.sortColumn === 'endDate'"
                            class="date">
                        <template v-if="item.status === 'preview'">
                          -
                        </template>
                        <template v-else>
                          {{ computedDate(item[header.sortColumn]) }}
                        </template>
                      </span>
                      <span v-else-if="header.sortColumn === 'answerCount'"
                            class="number">
                        <template v-if="item.status === 'preview'">
                          -
                        </template>
                        <template v-else>
                          {{ item[header.sortColumn] || 0 | thousand }}
                        </template>
                      </span>
                      <span v-else-if="header.sortColumn === 'status'"
                            class="date">
                        <span v-if="[SURVEY_CONSTANT.STATUS_RUNNING, SURVEY_CONSTANT.STATUS_STOP, SURVEY_CONSTANT.STATUS_WAITING].includes(item[header.sortColumn])"
                              @click="updateStatus(item)">
                          <ui-tooltip v-if="item[header.sortColumn] === SURVEY_CONSTANT.STATUS_STOP && hasRunning"
                                      arrowCenter
                                      :arrowBottom="index > 2"
                                      :arrowTop="index <= 2">
                            <span v-html="$t('userResearch.survey.onlyOne')"></span>
                          </ui-tooltip>
                          <ba-switch :value="item[header.sortColumn] !== SURVEY_CONSTANT.STATUS_STOP"
                                     :disabled="item[header.sortColumn] === SURVEY_CONSTANT.STATUS_STOP && hasRunning"
                                     :default="false"
                                     :offColor="'#d7d7d7'"
                                     :onColor="'#64b950'"
                                     :offLabel="'Stop'"
                                     :onLabel="'ON'"
                                     :width="56" />
                        </span>
                        <template v-else>
                          {{ $t(`userResearch.status.${item[header.sortColumn]}`) }}
                        </template>
                      </span>
                      <span v-else-if="header.sortColumn === 'view'"
                            class="view">
                        <button :disabled="item.status === 'preview'"
                                @click="go({ name: 'survey', query: { sid: domain.sid, survey: item.surveyId } })">
                          <i class="icon icon--arrow-body-right-green"></i>
                        </button>
                      </span>
                      <span v-else-if="header.sortColumn === 'more'">
                        <div class="more">
                          <i class="icon icon--hamburger"></i>
                          <ul :class="['more-options', { reverse: computedItems.length > 4 && index > computedItems.length - 4 }]">
                            <li v-if="item.status !== 'end'">
                              <button @click="editSurvey(item)" :disabled="isDemo" :class="{disabled: isDemo}">
                                {{$t('common.edit')}}
                              </button>
                            </li>
                            <li>
                              <button @click="deleteSurvey(item)" :disabled="isDemo" :class="{disabled: isDemo}">
                                {{$t('common.delete')}}
                              </button>
                            </li>
                            <li>
                              <button @click="duplicateSurvey(item)" :disabled="isDemo" :class="{disabled: isDemo}">
                                {{ $t('group.editMode.duplicate') }}
                              </button>
                            </li>
                          </ul>
                        </div>
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else
                 class="no-data"
                 :class="{ searching: keyword }">
              <template v-if="!keyword">
                <i class="icon loading--empty"></i>
                <p class="no-data-title"
                   v-html="$t('userResearch.survey.noSurvey')"></p>

                <ui-button :iconName="'icon--plus-red'"
                           :size="'large'"
                           :primary2="true"
                           @click="go({ name: 'addSurvey', query: { sid: $route.query.sid } })">
                  {{ $t('userResearch.survey.createOne') }}
                </ui-button>
              </template>
              <template v-else>
                <i class="icon icon--empty"></i>
                <h3 class="no-data-title"
                   v-html="$t('common.noResult')"></h3>
                <p class="no-data-title"
                   v-html="$t('common.noResultSearch2')"></p>
              </template>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <dimmed v-if="changingMainDomain">
      <main-domain :domain="domain"
                   @onClose="setMainDomain(false)"
                   @onChangeMainDomain="load(false)" />
    </dimmed>

    <dimmed v-if="deletingSurvey">
      <alert :title="deletingSurvey.status === 'preview' ? $t('userResearch.survey.alert.delete.previewTitle') : $t('userResearch.survey.alert.delete.title')"
             :description="deletingSurvey.status === 'preview' ? '' : $t('userResearch.survey.alert.delete.description')"
             :icon="'icon--trash-large'"
             :submit="$t('common.delete')"
             :cancel="$t('common.cancel')"
             @onSubmit="confirmDeleteSurvey"
             @onCancel="deleteSurvey(null)" />
    </dimmed>

    <dimmed v-if="isRunningSurvey">
      <alert :title="$t('userResearch.survey.alert.run.title')"
             :description="$t('userResearch.survey.alert.run.description')"
             :icon="'icon--dialog-warning'"
             :submit="$t('common.confirm')"
             @onSubmit="isRunningSurvey = false"
      />
    </dimmed>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import { getDomains, getData } from '@/api/domain'
import { EventBus } from '@/libs/event-bus'
import { getSurveyList, updateSurveyStatus, deleteSurvey } from '@/api/survey'

import MainDomain from '@/components/dashboard/MainDomain'
import UiTooltip from '@/components/ui/UiTooltip'
import UiButton from '@/components/ui/UiButton'
import BaSwitch from '@/components/ui/ba/Switch'
import Dimmed from '@/components/ui/Dimmed'
import Alert from '@/components/ui/Alert'
import BaLoading from '@/components/ui/ba/Loading'
import TextHighlight from '@/components/ui/TextHighlight'
import { SURVEY_CONSTANT } from '@/libs/constants'
import { dateFormatTimezone } from '@/libs/utils'

export default {
  name: 'UserResearch',
  components: {
    MainDomain,
    UiTooltip,
    UiButton,
    BaSwitch,
    Dimmed,
    Alert,
    BaLoading,
    TextHighlight
  },
  created () {
    EventBus.$emit('setTitle', this.$t('userResearch.title'))
    this.load()
  },
  methods: {
    ...mapMutations([
      'setSurveyDetail',
      'setSurveyList'
    ]),
    computedDate (value) {
      return dateFormatTimezone(value, this.domain.timezoneName, 'YYYY-MM-DD')
    },
    load (init = true) {
      let sid = this.$route.query.sid

      this.loading = true
      Promise.all([
        getDomains(sid, !init).then(result => {
          for (const domain of result) {
            if (domain.mainDomain) {
              this.mainDomain = domain.orgSid
              break
            }
          }
        }),
        getData(sid, !init).then(result => {
          this.domain = {
            sid: result.orgSid || result.sid,
            orgSid: result.orgSid,
            realUrl: result.realUrl || result.domain,
            domain: result.domain,
            domainName: result.domainName,
            timezone: result.timezone,
            timezoneName: result.timezoneName
          }
        })
      ]).then(() => {
        if (!init || !this.surveyList || !this.surveyList[sid]) {
          getSurveyList({ sid }, true).then(result => {
            this.afterLoad(result)
            this.setSurveyList([sid, result])
          })
        } else {
          this.afterLoad(this.surveyList[sid])
        }
      })
    },
    afterLoad (result) {
      this.items = []
      for (const survey of result) {
        if (survey) {
          if (survey.status === SURVEY_CONSTANT.STATUS_PREVIEW) {
            survey.startDate = '-'
            survey.endDate = '-'
            survey.answerCount = -1
          }
          this.items.push(survey)
        }
      }
      this.sortList()
      this.calcHasRunning()
      this.loading = false
      setTimeout(() => {
        EventBus.$emit('onLoading', false)
      }, 600)
    },
    toDashboard () {
      EventBus.$emit('onLoading', true)
      setTimeout(() => {
        let query = {}
        if (this.domain.sid) {
          query.sid = this.domain.sid
        }
        this.$router.push({ name: 'dashboard', query })
      }, 300)
    },
    setMainDomain (value) {
      if (!value || this.domain.orgSid !== this.mainDomain) {
        this.changingMainDomain = value
      }
    },
    onSelectTabType (tabType) {
      // this.tabType = tabType
    },

    changeSort (sort) {
      if (!this.notSortable.includes(sort)) {
        if (this.sortColumn === sort) {
          this.isSortAsc = !this.isSortAsc
        } else {
          this.sortColumn = sort
          this.isSortAsc = false
        }
        this.sortList()
      }
    },
    sortList () {
      if (this.sortColumn === 'default') {
        this.items.sort((a, b) => {
          const sortByRegDate = new Intl.Collator('en').compare(b.regDate.toString(), a.regDate.toString())
          if (!sortByRegDate) {
            const sortByStartDate = new Intl.Collator('en').compare(b.start.toString(), a.start.toString())
            if (!sortByStartDate) {
              const sortByTitle = new Intl.Collator('en').compare(b.surveyName.toString(), a.surveyName.toString())
              return sortByTitle
            }
            return sortByStartDate
          }
          return sortByRegDate
        })
      } else {
        this.items.sort((a, b) => {
          if (typeof a[this.sortColumn] === 'string' && typeof b[this.sortColumn] === 'string') {
            if (this.isSortAsc) {
              return new Intl.Collator('en').compare(a[this.sortColumn].toString(), b[this.sortColumn].toString())
            } else {
              return new Intl.Collator('en').compare(b[this.sortColumn].toString(), a[this.sortColumn].toString())
            }
          } else {
            if (this.isSortAsc) {
              return a[this.sortColumn] > b[this.sortColumn] ? 1 : -1
            } else {
              return b[this.sortColumn] > a[this.sortColumn] ? 1 : -1
            }
          }
        })
      }
    },
    updateStatus (survey) {
      if (survey) {
        if (survey.status === SURVEY_CONSTANT.STATUS_RUNNING || survey.status === SURVEY_CONSTANT.STATUS_WAITING || !this.hasRunning) {
          let to = survey.status === SURVEY_CONSTANT.STATUS_STOP ? SURVEY_CONSTANT.STATUS_WAITING : SURVEY_CONSTANT.STATUS_STOP

          updateSurveyStatus({
            sid: this.domain.orgSid,
            surveyId: survey.surveyId,
            status: to
          }, true).then(result => {
            if (result.runningCount) {
              this.isRunningSurvey = true
            } else {
              let surveyList = this.surveyList[this.domain.orgSid]
              for (let i = 0; i < surveyList.length; i++) {
                if (surveyList[i] && surveyList[i].surveyId === survey.surveyId) {
                  surveyList[i].status = to
                }
              }
              this.afterLoad(surveyList)
              this.setSurveyList([this.domain.orgSid, surveyList])

              let key = `${this.domain.orgSid}-${survey.surveyId}`
              if (this.surveyDetail[key]) {
                this.setSurveyDetail([key, {
                  ...this.surveyDetail[key],
                  status: to
                }])
              }
            }
          })
        }
      }
    },
    editSurvey (survey) {
      const query = { sid: this.domain.sid, survey: survey.surveyId, tempSurveyId: survey.tempSurveyId }
      this.go({ name: 'addSurvey', query })
    },
    deleteSurvey (survey) {
      this.deletingSurvey = survey
    },
    duplicateSurvey (survey) {
      this.go({ name: 'addSurvey', query: { sid: this.domain.sid, duplicate: survey.surveyId, duplicateTemp: survey.tempSurveyId } })
    },
    confirmDeleteSurvey () {
      const query = {
        sid: this.domain.orgSid
      }
      if (this.deletingSurvey.status === SURVEY_CONSTANT.STATUS_PREVIEW) {
        query.tempSurveyId = this.deletingSurvey.tempSurveyId
        query.isTemp = true
      } else {
        query.surveyId = this.deletingSurvey.surveyId
      }
      deleteSurvey(query, true).then(() => {
        let surveyList = this.surveyList[this.domain.orgSid]
        for (let i = 0; i < surveyList.length; i++) {
          if (surveyList[i] &&
            ((!this.deletingSurvey.tempSurveyId && surveyList[i].surveyId === this.deletingSurvey.surveyId) ||
              (this.deletingSurvey.tempSurveyId && surveyList[i].tempSurveyId === this.deletingSurvey.tempSurveyId))) {
            delete surveyList[i]
          }
        }
        this.afterLoad(surveyList)
        this.deleteSurvey(null)
        this.setSurveyList([this.domain.orgSid, surveyList])
      })
    },
    go (params) {
      EventBus.$emit('onLoading', true)
      setTimeout(() => {
        this.$router.push(params)
      }, 300)
    },
    startSearch () {
      this.$refs.search.focus()
    },
    calcHasRunning () {
      this.hasRunning = false
      for (const survey of this.items) {
        if ([SURVEY_CONSTANT.STATUS_RUNNING, SURVEY_CONSTANT.STATUS_WAITING].includes(survey.status)) {
          this.hasRunning = true
          break
        }
      }
    }
  },
  computed: {
    ...mapState([
      'surveyList',
      'surveyDetail',
      'scrollbarWidth',
      'isDemo'
    ]),
    computedItems () {
      if (!this.keyword) {
        return this.items
      }
      return this.items.filter(item => item.surveyName.includes(this.keyword))
    },
    computedSearching () {
      return this.searching || this.keyword
    },
    headers () {
      return [
        { name: this.$t('userResearch.survey.column.subject'), sortColumn: 'surveyName' },
        { name: this.$t('userResearch.survey.column.startDate'), sortColumn: 'startDate' },
        { name: this.$t('userResearch.survey.column.endDate'), sortColumn: 'endDate' },
        { name: this.$t('userResearch.survey.column.responses'), sortColumn: 'answerCount' },
        { name: this.$t('userResearch.survey.column.status'), sortColumn: 'status' },
        { name: this.$t('userResearch.survey.column.view'), sortColumn: 'view' },
        { name: '', sortColumn: 'more' }
      ]
    }
  },
  data () {
    return {
      SURVEY_CONSTANT: SURVEY_CONSTANT,
      domain: null,
      mainDomain: null,
      changingMainDomain: false,
      tabType: 'survey',
      notSortable: ['view', 'more'],
      sortColumn: 'default',
      isSortAsc: false,
      items: [],
      deletingSurvey: null,
      keyword: '',
      searching: false,
      hasRunning: false,
      loading: true,
      isRunningSurvey: false
    }
  }
}
</script>

<style lang="scss" scoped>
.user-research {
  width: 100%;
  .user-research-wrap {
    padding: 14px 30px;
  }
  .user-research-header {
    padding: 55px 30px 0;

    .back {
      display: inline-block;
      vertical-align: middle;
      padding-right: 10px;

      & /deep/ .ui-btn {
        width: 34px;
        height: 34px;
        min-width: 1px;
        padding: 0;

        .icon {
          left: 50%;
          transform: translate(-50%, -50%);
        }
      }
    }

    .title {
      color: #2f2f2f;
      position: relative;
      display: inline-block;
      vertical-align: middle;

      .main-button {
        width: 30px;
        height: 30px;
        display: inline-block;
        vertical-align: middle;
        padding-right: 9px;
        transition: padding-right 0.15s ease;

        .to-main {
          width: 30px;
          height: 30px;
          border-radius: 15px;
          background-color: transparent;
          vertical-align: middle;
          position: relative;
          cursor: default;
          transition: box-shadow 0.15s ease;

          .icon {
            position: absolute;
            top: 5px;
            left: 5px;
            transition: opacity 0.15s ease;

            &.off {
              opacity: 0;
            }
          }
        }

        &.off {
          padding-right: 13px;

          .to-main {
            box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.17), 0 0 1px 0 rgba(0, 0, 0, 0.2);
            cursor: pointer;

            .icon {
              &.off {
                opacity: 1;
              }

              &:not(.off) {
                opacity: 0;
              }
            }
          }
        }
      }

      .name {
        line-height: 30px;
        margin: -3px 0;
        font-size: 24px;
        font-weight: 500;
        display: inline-block;
        vertical-align: middle;
        max-width: 400px;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .domain {
        line-height: 30px;
        margin: -3px 0;
        font-weight: 500;
        display: inline-block;
        vertical-align: middle;
        font-size: 20px;
        max-width: 200px;
        overflow: hidden;
        text-overflow: ellipsis;

        &::before {
          content: "";
          display: inline-block;
          vertical-align: middle;
          width: 1px;
          height: 15px;
          background-color: #c1c1c1;
          margin: -5px 15px 0;
        }
      }
    }
  }

  .user-research-body {
    position: relative;
    min-height: 560px;
    max-height: calc(100vh - 260px);
    padding: 19px 20px 30px;
    border-radius: 14px;
    box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.08);
    background-color: #fff;

    width: 100%;
    box-sizing: border-box;
    margin-top: 14px;
    display: flex;
    flex-direction: column;

    .loader {
      position: absolute;
      top: 0;
      left: 0;
      z-index: 100;
      width: 100%;
      height: 100%;
      border-radius: 14px;
      background-color: #ffffff;

      &.fade-enter-active, &.fade-leave-active {
        opacity: 1;
        transition: opacity 0.2s ease;
      }

      &.fade-enter, &.fade-leave-to{
        opacity: 0;
      }
    }

    .body-header {
      flex-shrink: 0;
      .tab--wrap {
        display: inline-block;

        .tab {
          display: inline-block;
          width: 250px;
          border: 1px solid #e0e0e0;
          background-color: #ffffff;
          transition: background-color 0.2s cubic-bezier(0, 0, 0, 1), border-color 0.2s cubic-bezier(0, 0, 0, 1);
          border-radius: 20px;
          position: relative;

          button {
            width: 125px;
            text-align: center;
            line-height: 36px;
            height: 36px;
            color: #dddddd;
            position: relative;
            font-size: 15px;
            z-index: 2;
            transition: color 0.2s cubic-bezier(0, 0, 0, 1);
            cursor: default;
            overflow: visible;

            &:first-child {
              box-sizing: border-box;
              padding-left: 5px;
            }

            &:last-child {
              box-sizing: border-box;
            }

            &.on {
              color: #ffffff;
            }

            .soon {
              position: absolute;
              top: 0;
              left: 50%;
              transform: translate(-50%, -50%);
              padding: 2px 6px;
              border-radius: 7.5px;
              box-shadow: 0 3px 4px -3px rgba(0, 0, 0, 0.35);
              background-image: linear-gradient(to bottom, #1199af 25%, #058ca2);
              font-size: 9px;
              line-height: 1.11;
              letter-spacing: -0.28px;
              color: #eee;
              white-space: nowrap;
            }
          }

          &::before {
            content: "";
            position: absolute;
            z-index: 1;
            top: -1px;
            left: -1px;
            background: #ec0147;
            border-radius: 20px;
            transition: left 0.2s cubic-bezier(0, 0, 0, 1);
            display: block;
            width: 130px;
            height: 38px;
            box-shadow: 0 12px 10px -4px rgba(0, 0, 0, 0.2);
          }

          &.survey::before {
            left: 120px;
          }
        }
      }

      .buttons {
        float: right;
        margin-top: -1px;

        .search {
          float: left;
          position: relative;
          margin-right: 8px;
          border-radius: 20px;
          border: solid 1px #bbb;
          background-color: #fff;
          width: 38px;
          transition: width 0.3s cubic-bezier(0, 0, 0, 1);
          overflow: hidden;

          &.ing {
            width: 169px;

            input {
              cursor: text;
            }
          }

          input {
            line-height: 38px;
            width: 114px;
            height: 38px;
            padding: 0 38px 0 18px;
            border-radius: 20px;
            border: none;
            background-color: transparent;
            font-size: 14px;
            color: #444;
            cursor: pointer;
          }

          .icon {
            position: absolute;
            top: 0;
            right: 0;
            z-index: 10;
            cursor: pointer;
            width: 38px;
            height: 38px;
            background-size: 16px 16px;
          }
        }

        .add {
          float: left;
          position: relative;

          .bubble {
            position: absolute;
            top: -80px;
            right: 0;
            opacity: 0;
            pointer-events: none;
            transform: translateY(15px);
            transition: opacity 0.2s ease,
                        transform 0.2s ease;
          }

          &:hover {
            .bubble {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .ui-btn {
            & /deep/ svg {
              margin: 3px 4px -3px -4px;
            }

            & /deep/ g {
              transition: fill 0.15s ease;
              fill: #ec0147;
            }

            &:hover {
              & /deep/ g {
                fill: #ffffff;
              }
            }

            &.has {
              & /deep/ g {
                fill: #ffffff;
              }
            }
          }
        }
      }
    }

    .body-list {
      position: relative;
      margin-top: 13px;

      .tbody {
        height: 100vh;
        max-height: calc(100vh - 395px);
        overflow-y: scroll;
        overflow-x: hidden;
        min-height: 460px;

        &::after {
          content: "";
          position: absolute;
          bottom: 0;
          left: 0;
          width: calc(100% - 20px);
          height: 17px;
          background-image: linear-gradient(to bottom, rgba(255, 255, 255, 0), #fff);
        }

        table {
          margin-bottom: 10px;
        }
      }

      table {
        width: 100%;

        th {
          text-align: center;
          padding: 0 10px;
          height: 50px;
          background-color: #555;
          font-size: 13px;
          font-weight: normal;
          line-height: 1.23;
          color: #fff;
          white-space: nowrap;

          &:first-child {
            border-radius: 5px 0 0 5px;
            padding-left: 15px;
          }

          &:last-child {
            border-radius: 0 5px 5px 0;
            padding-right: 15px;
          }

          &.th-surveyName {
            text-align: left;
          }

          &.sortable {
            cursor: pointer;
          }

          &.th-status,
          &.th-startDate,
          &.th-endDate {
            span {
              min-width: 40px;
              padding-left: 10px;
            }
          }

          span {
            display: inline-block;
            vertical-align: middle;
          }

          button {
            vertical-align: middle;
          }

          .sort {
            display: inline-block;
            width: 14px;
            height: 18px;
            margin-top: -2px;
            margin-left: 4px;
            vertical-align: middle;
            border-radius: 5px;
            transition: background-color 0.1s ease,
                        box-shadow 0.1s ease;

            i.icon {
              float: left;
              opacity: 0.2;
              transition: opacity 0.15s ease;
            }

            &.active {
              &.asc {
                .icon--sort-asc {
                  opacity: 1;
                }
              }

              &.desc {
                .icon--sort-desc {
                  opacity: 1;
                }
              }
            }

            &.active {
              background-color: #272727;
              box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.146);
            }
          }
        }

        td {
          padding: 19px 10px;
          border-bottom: 1px solid #ebebeb;
          font-size: 14px;
          line-height: 1;
          color: #2f2f2f;
          white-space: nowrap;

          &.td-status,
          &.td-more,
          &.td-view {
            padding: 0;
            text-align: center;
          }

          .more {
            position: relative;

            .icon {
              cursor: pointer;
              border-radius: 30px;
              padding: 6px;
              background-size: 20px 20px;
            }

            .more-options {
              position: absolute;
              top: 32px;
              right: 1px;
              z-index: 10;
              transform: translate(0, -10px) scaleZ(0);
              opacity: 0;
              width: 80px;
              border-radius: 6px;
              box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.25);
              background-color: #fff;
              transform-origin: top center;
              transition: opacity 0.3s ease,
                          transform 0.3s ease;

              &.reverse {
                top: auto;
                bottom: 32px;
                transform: translate(0, 10px) scaleZ(0);
              }

              button {
                width: 100%;
                font-size: 14px;
                padding: 11px 0;
                color: #444;
                background-color: #fff;
                transition: background-color 0.1s ease;

                &:hover {
                  background-color: #ebebeb;
                }

                &.disabled {
                  background-color: #fff;
                  color: #C7C7C7;
                  pointer-events: none;
                  cursor: default;
                }
              }

              li {
                &:first-child {
                  button {
                    border-top-left-radius: 7px;
                    border-top-right-radius: 7px;
                  }
                }

                &:last-child {
                  button {
                    border-bottom-left-radius: 7px;
                    border-bottom-right-radius: 7px;
                  }
                }
              }
            }

            &:hover {
              .icon {
                background-color: #f2f2f2;
              }

              .more-options {
                transform: translate(0, 0) scaleZ(1) !important;
                opacity: 1;
              }
            }
          }

          span {
            position: relative;
            display: block;

            &.name {
              span {
                width: calc(100vw - 665px);
                min-width: 585px !important;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              }
            }

            &.date {
              font-weight: normal;
              text-align: center;
              font-size: 13px;
              color: #888;
            }

            &.number {
              font-weight: normal;
              text-align: right;
              font-size: 13px;
              color: #444;
            }

            &.view {
              button {
                width: 34px;
                height: 34px;
                padding: 8px;
                border-radius: 20px;
                border: solid 1px #57ab00;
                background-color: #fff;
                transition: background-color 0.1s ease;

                &:hover:not([disabled]) {
                  background-color: #f2f2f2;
                }

                &[disabled] {
                  cursor: default;
                  opacity: 0.3;
                }
              }
            }

            .bubble {
              position: absolute;
              bottom: 38px;
              left: 50%;
              transform: translate(-50%, 20px);
              opacity: 0;
              pointer-events: none;
              transition: transform 0.2s ease,
                          opacity 0.2s ease;

              & /deep/ .text {
                text-align: left;
              }

              &.top {
                bottom: auto;
                top: 38px;
                transform: translate(-50%, -20px);
              }
            }

            &:hover {
              .bubble {
                transform: translate(-50%, 0);
                opacity: 1;

                &.top {
                  transform: translate(-50%, 0);
                }
              }
            }
          }

          &:first-child {
            padding-left: 15px;
          }
        }
      }
    }

    .no-data {
      text-align: center;
      font-size: 13px;
      line-height: 1.77;
      color: #444;
      padding: 70px 0;

      p {
        padding-bottom: 25px;
        margin-top: -5px;
      }

      &.searching {
        padding: 150px 0 0;

        h3 {
          padding-bottom: 10px;
          margin-top: 5px;
          font-size: 20px;
        }

        p {
          font-size: 14px;
          color: #999;
        }
      }
    }
  }

  .slide-fade-enter-active {
    transition: opacity 0.5s ease, transform 0.5s ease, top 0.5s ease;
    transition-delay: 0.2s;
    z-index: 1;
  }
  .slide-fade-leave-active {
    transition: opacity 0.3s ease, transform 0.3s ease, top 0.3s ease;
    z-index: 1;
  }

  .slide-fade-enter,
  .slide-fade-leave-to {
    top: calc(50% - 30px);
    opacity: 0;
    transform: translate(-50%, -50%);
  }
}
</style>
